import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CodeIcon from "@mui/icons-material/Code";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const USER_TYP_ADMIN = 1;

const AUDIENCE_BOXES = [
  {
    id: "api-docs-partner",
    icon: MenuBookIcon,
    titleKey: "pages.systemSettings.apiDocsPartnerTitle",
    descriptionKey: "pages.systemSettings.apiDocsPartnerDescription",
    path: "/dsok/system-settings/developer/api-docs/partner",
  },
  {
    id: "api-docs-internal",
    icon: CodeIcon,
    titleKey: "pages.systemSettings.apiDocsInternalTitle",
    descriptionKey: "pages.systemSettings.apiDocsInternalDescription",
    path: "/dsok/system-settings/developer/api-docs/internal",
  },
];

function ChoiceBox({ icon: Icon, titleKey, descriptionKey, path }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: "box-shadow 0.2s, background-color 0.2s",
        "&:hover": { boxShadow: 1, bgcolor: "action.hover" },
      }}
      onClick={() => navigate(path)}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "action.hover",
              flexShrink: 0,
            }}
          >
            {Icon && <Icon sx={{ fontSize: 28, color: "primary.main" }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t(titleKey)}
            </Typography>
            {descriptionKey && (
              <Typography variant="body2" color="text.secondary">
                {t(descriptionKey)}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ApiDocsChooserPage() {
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const isAdmin = Number(authUser?.type ?? authUser?.typ) === USER_TYP_ADMIN;

  const title = t("pages.systemSettings.apiDocsTitle", {
    defaultValue: "API Dokumentácia",
  });

  if (!isAdmin) {
    return (
      <Box sx={{ px: 0 }}>
        <Alert severity="error">
          {t("pages.apiDocs.noAccess", {
            defaultValue: "Táto sekcia je dostupná len pre administrátorov.",
          })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            {t("sidebar.menuItem.home")}
          </Link>
          <Link
            to="/dsok/system-settings"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("pages.systemSettings.title", { defaultValue: "Nastavenia systému" })}
          </Link>
          <Typography color="text.primary">{title}</Typography>
        </Breadcrumbs>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Grid container spacing={2}>
        {AUDIENCE_BOXES.map((box) => (
          <Grid item xs={12} sm={6} md={3} key={box.id}>
            <ChoiceBox
              icon={box.icon}
              titleKey={box.titleKey}
              descriptionKey={box.descriptionKey}
              path={box.path}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
